import React, { createContext, useRef, useContext, useEffect, useReducer, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { eventPollGet } from '../../../api/player';
import { useEventContext } from './EventContext';
import { useStreamContext } from './StreamContext';
import { Event } from '../../../cp/components/Comet';
import storage from '../../../player/js/storage';
import cloneDeep from 'lodash.clonedeep';
import { createGetterFromSetState } from '../helpers/createGetterFromSetState';
import { ObjectStoreService } from '../utils/ObjectStoreService';
import { BROADCAST_DELAY_SECONDS } from '../../../common/src/constants';
import { MODULE_POLLS } from '../components/SideModule/Tabs';
import { EVENT_TYPE_RESTORED } from '../../../player/js/CometProvider/GoCometProvider';

export const POLLS_ACTION_LOAD = 'POLLS_LOAD';
export const POLLS_ACTION_CREATE = 'POLLS_CREATE';
export const POLLS_ACTION_UPDATE = 'POLLS_UPDATE';
export const POLLS_ACTION_DELETE = 'POLLS_DELETE';
export const POLLS_ACTION_ANSWER_CREATE = 'POLLS_ANSWER_CREATE';
export const POLLS_ACTION_ANSWER_UPDATE = 'POLLS_ANSWER_UPDATE';
export const POLLS_ACTION_ANSWER_DELETE = 'POLLS_ANSWER_DELETE';

export const pollsActionLoad = (polls) => ({ type: POLLS_ACTION_LOAD, polls });
export const pollsActionCreate = (poll) => ({
    type: POLLS_ACTION_CREATE,
    poll,
});
export const pollsActionUpdate = (poll) => ({
    type: POLLS_ACTION_UPDATE,
    poll,
});
export const pollsActionDelete = (poll) => ({
    type: POLLS_ACTION_DELETE,
    poll,
});
export const pollsActionAnswerCreate = (answer) => ({
    type: POLLS_ACTION_ANSWER_CREATE,
    answer,
});
export const pollsActionAnswerUpdate = (answer) => ({
    type: POLLS_ACTION_ANSWER_UPDATE,
    answer,
});
export const pollsActionAnswerDelete = (answer) => ({
    type: POLLS_ACTION_ANSWER_DELETE,
    answer,
});

const updatePoll = (polls, poll) => {
    const index = polls.findIndex(({ id }) => id === poll.id);
    if (-1 === index) {
        return polls.concat([poll]);
    }

    polls = polls.slice();
    polls[index] = { ...polls[index], ...poll };

    return polls;
};

const deletePoll = (polls, poll) => {
    return polls.filter(({ id }) => id !== poll.id);
};

const updateAnswer = (polls, answer) => {
    polls = polls.slice();
    const pollIndex = polls.findIndex(({ id }) => id === answer.pollId);
    if (-1 === pollIndex) {
        return polls;
    }
    if (polls[pollIndex].answer === undefined) {
        polls[pollIndex].answer = []
    }
    if (polls[pollIndex].answer) {
        const answerIndex = polls[pollIndex].answer.findIndex(({ id }) => id === answer.id);
        if (-1 === answerIndex) {
            polls[pollIndex].answer.push(answer);
        } else {
            polls[pollIndex].answer[answerIndex] = {
                ...polls[pollIndex].answer[answerIndex],
                ...answer,
            };
        }
    }

    return polls;
};
const deleteAnswer = (polls, answer) => {
    const pollIndex = polls.findIndex(({ id }) => id === answer?.pollId);
    if (-1 === pollIndex) {
        console.error(`Poll not found with id: ${answer.pollId}`, polls);
        return polls;
    } else {
        return polls.map(poll => poll.id === answer.pollId ? { ...poll, answer: (poll.answer || []).filter(({ id }) => id !== answer.id) } : poll);
    }
};

const pollsReducer = (state, action) => {
    switch (action.type) {
        case POLLS_ACTION_LOAD:
            return state.length > 0 ? action.polls.reduce(updatePoll, state) : state.concat(action.polls);
        case POLLS_ACTION_CREATE:
        case POLLS_ACTION_UPDATE:
            return updatePoll(state, action.poll);
        case POLLS_ACTION_DELETE:
            return deletePoll(state, action.poll);

        case POLLS_ACTION_ANSWER_CREATE:
        case POLLS_ACTION_ANSWER_UPDATE:
            return updateAnswer(state, action.answer);
        case POLLS_ACTION_ANSWER_DELETE:
            return deleteAnswer(state, action.answer);
        default:
            return state;
    }
};

export const PollsContext = createContext([]);

export const PollsProvider = ({ children }) => {
    const { event } = useEventContext();
    const { stream } = useStreamContext();
    const { pathname, search } = useLocation();
    const [polls, dispatch] = useReducer(pollsReducer, []);
    const [isLoading, setLoading] = useState(true);
    const storageKeyPolls = `event/${event.eventKey}/polls/voted`;
    const storageKeyResults = `event/${event.eventKey}/polls/viewedResult`;
    const storageKeyReadPolls = `event/${event.eventKey}/polls/read`;
    const [viewPollIds, setViewPollIds] = useState([]);
    const [answeredPollIds, setAnsweredPollIds] = useState(JSON.parse(storage.get(storageKeyPolls)) || []);
    const [viewedResultPolls, setViewedResultPolls] = useState(JSON.parse(storage.get(storageKeyResults)) || []);
    const [closedPolls, setClosedPolls] = useState([]);
    const [closedResultPolls, setClosedResultPolls] = useState([]);
    const [readPollIds, setReadPollIds] = useState(JSON.parse(storage.get(storageKeyReadPolls)) || []);
    const getCurrentAnsweredPollIds = createGetterFromSetState(setAnsweredPollIds);
    const [readCurrentPollIds, setReadCurrentPollIds] = useState([]);
    const [skipPollIds, setSkipViewPollIds] = useState([]);
    const pollsRef = useRef(polls);
    const [, , , pageName] = pathname.split('/');
    const pageNameRef = useRef(pageName);
    const params = new URLSearchParams(search);
    const currentViewPoll = params.get('view-poll-id');
    const isViewPollActive = currentViewPoll === "active";
    const isPollsEnabled = params.get('module-active') === MODULE_POLLS;
    const navigate = useNavigate();

    useEffect(() => {
        pageNameRef.current = pageName;
    }, [pageName]);

    useEffect(() => {
        pollsRef.current = polls;
    }, [polls]);

    useEffect(() => {
        pollsGet();
    }, []);

    useEffect(() => {
        storage.set(storageKeyPolls, JSON.stringify(answeredPollIds), storage.MONTH);
    }, [answeredPollIds]);

    useEffect(() => {
        storage.set(storageKeyResults, JSON.stringify(viewedResultPolls), storage.MONTH);
    }, [viewedResultPolls]);

    const currentPolls = useMemo(() =>
        polls.filter((poll) => {
            poll.answer = poll.answer?.sort((a, b) => (a.order === b.order ? a.id - b.id : a.order - b.order)) || null;

            return poll.streamId === stream?.id;
        })
            .sort((a, b) => (a.order === b.order ? a.id - b.id : a.order - b.order)
            ), [polls, stream]);

    const activePolls = useMemo(() => {
        return currentPolls.filter((poll) => 'active' === poll.status && !skipPollIds.includes(poll.id));
    }, [currentPolls, skipPollIds]);

    const answeredPolls = useMemo(() => {
        return activePolls.filter(poll => answeredPollIds.includes(poll.id) && (!poll.display || poll.display === pageName));
    }, [activePolls, answeredPollIds, pageName]);

    const viewPolls = useMemo(() => {
        return activePolls.filter(poll => viewPollIds.includes(poll.id) && (!poll.display || poll.display === pageName));
    }, [activePolls, viewPollIds, pageName]);

    const unreadPolls = useMemo(() => {
        return activePolls.filter(poll => !readPollIds.includes(poll.id) && (!poll.display || poll.display === pageName));
    }, [activePolls, readPollIds, pageName]);

    const defaultPolls = useMemo(() => {
        return activePolls.filter(poll =>
            readPollIds.includes(poll.id) && !answeredPollIds.includes(poll.id) && (!poll.display || poll.display === pageName)
        );
    }, [activePolls, readPollIds, answeredPollIds, pageName]);

    const resultPolls = currentPolls.filter(
        (poll) =>
            ('result' === poll.status && !closedResultPolls.includes(poll.id)) ||
            ('active' === poll.status &&
                -1 !== answeredPollIds.indexOf(poll.id) &&
                -1 === viewedResultPolls.indexOf(poll.id)),
    );

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (!currentViewPoll) {
            const poll = activePolls.find(({ id }) => ![...answeredPollIds, ...closedPolls].includes(id))
            if (poll) {
                const delay = Math.floor(poll.statusChangedAt + BROADCAST_DELAY_SECONDS - (Date.now() / 1000)) * 1000;
                setTimeout(() => setViewPollIds(prev => [...prev, poll.id]), Math.max(delay, 0));
            }
        } else {
            if (!activePolls.find(({ id }) => String(id) === String(currentViewPoll))) {
                setViewPollIds(prev => prev.filter(id => String(id) !== String(currentViewPoll)))
                navigate(location.pathname);
            }
        }

    }, [answeredPollIds, activePolls, currentViewPoll, closedPolls, isLoading])

    async function pollsGet() {
        setLoading(true);
        const polls = await eventPollGet(event.eventKey);
        dispatch(pollsActionLoad(polls));
        setLoading(false);
    }

    const onAnswer = (poll) => {
        setAnsweredPollIds((answeredPollIds) => answeredPollIds.concat([poll.id]));
        // setViewPollIds((polls) => polls.filter((id) => id !== poll.id));

        const timerId = ObjectStoreService.get(`poll_id_${poll.id}`);
        if (timerId) {
            clearTimeout(timerId);
            ObjectStoreService.delete(`poll_id_${poll.id}`);
        }

        const pollIds = readPollIds.concat([poll.id]);
        storage.set(storageKeyReadPolls, JSON.stringify(pollIds), storage.MONTH);
        setReadPollIds(pollIds);
    };

    const onCloseResultPoll = async (poll) => {
        if (!viewedResultPolls.includes(poll.id)) {
            setViewedResultPolls(prev => [...prev, poll.id]);
        }
        if (poll.status === "result" && !closedResultPolls.includes(poll.id)) {
            setClosedResultPolls(prev => [...prev, poll.id])
        }
    };

    useEffect(() => {
        if (isViewPollActive) {
            setViewPollIds((pollIds) => {
                const newPollIds = activePolls
                    ?.filter(({ id }) => !pollIds.includes(id))
                    ?.map(({ id }) => id);

                return [...pollIds, ...newPollIds];
            });
        }
    }, [isViewPollActive, activePolls]);

    const removePollInSkip = (pollId) => {
        setSkipViewPollIds((pollIds) => pollIds.filter((id) => id !== pollId));
    };

    const addPollInView = (pollId) => {
        removePollInSkip(pollId);
        setViewPollIds((polls) => polls.concat([pollId]));
    };

    const removePollInView = (pollId) => {
        removePollInSkip(pollId);
        setViewPollIds((polls) => polls.filter((id) => id !== pollId));
    };

    const onUpdatePoll = async (poll) => {
        const pollElement = pollsRef.current?.find(item => item.id === poll.id);

        if (pollElement && pollElement.status !== 'active' && poll.status === 'active') {
            // Запуск опроса

            const answeredPollIds = await getCurrentAnsweredPollIds();
            if (!answeredPollIds?.includes(poll.id)) {
                const delay = BROADCAST_DELAY_SECONDS - ((Date.now() / 1000) - poll.statusChangedAt);
                const isView = !poll.display || pageNameRef.current === poll.display;

                if (isView) {
                    if (delay > 0 && !poll.display && pageNameRef.current === "broadcast") {
                        setSkipViewPollIds(pollIds => pollIds.concat(poll.id));

                        // Задержка отображения опроса
                        const timerId = setTimeout(() => addPollInView(poll.id), delay * 1000);

                        ObjectStoreService.set(timerId, `poll_id_${poll.id}`);
                    } else {
                        addPollInView(poll.id);
                    }
                }
            }
        } else if (pollElement && pollElement.status === 'active' && poll.status !== 'active') {
            // Остановка опроса

            const timerId = ObjectStoreService.get(`poll_id_${poll.id}`);
            if (timerId) {
                clearTimeout(timerId);
                ObjectStoreService.delete(`poll_id_${poll.id}`);
            }

            removePollInView(poll.id);
        } else if (pollElement && pollElement.status === 'stop' && poll.status === 'result') {
            // Повторный запуск результатов опроса

            setClosedResultPolls(prev => prev.filter((id) => id !== poll.id));
            setViewedResultPolls(prev => prev.filter((id) => id !== poll.id));
        }

        dispatch(pollsActionUpdate(poll));
    };

    const onViewPoll = useCallback((poll) => {
        setViewPollIds((polls) => polls.filter((id) => id !== poll.id));
    }, []);

    const onClosePoll = (poll) => poll && setClosedPolls(prev => [...prev, poll.id])

    const onReadPoll = useCallback((pollId) => {
        const pollIds = readPollIds.concat([pollId]);
        storage.set(storageKeyReadPolls, JSON.stringify(pollIds), storage.MONTH);
        setReadCurrentPollIds((polls) => polls.concat([pollId]));
    }, [readPollIds]);

    const onUpdateReadPolls = useCallback(() => {
        const pollIds = readPollIds.concat([...readCurrentPollIds]);
        setReadPollIds(pollIds);
        setReadCurrentPollIds([]);
    }, [readCurrentPollIds, readPollIds]);

    const context = {
        allPolls: polls,
        activePolls,
        resultPolls,
        viewedResultPolls,
        onAnswer,
        onCloseResultPoll,
        isLoading,
        enabled: !!currentPolls?.length || isPollsEnabled,
        onViewPoll,
        onClosePoll,
        answeredPollIds,
        answeredPolls,
        setAnsweredPollIds,
        viewPolls,
        unreadPolls,
        defaultPolls,
        onReadPoll,
        onUpdateReadPolls,
        readCurrentPollIds,
        readPollIds,
    };

    return (
        <PollsContext.Provider value={context}>
            <Event event={`create:poll`} handler={(poll) => dispatch(pollsActionCreate(poll))} />
            <Event event={`update:poll`} handler={(poll) => onUpdatePoll(poll)} />
            <Event event={`delete:poll`} handler={(poll) => dispatch(pollsActionDelete(poll))} />
            <Event event={`create:poll/answer`} handler={(answer) => dispatch(pollsActionAnswerCreate(answer))} />
            <Event event={`update:poll/answer`} handler={(answer) => dispatch(pollsActionAnswerUpdate(answer))} />
            <Event event={`delete:poll/answer`} handler={(answer) => dispatch(pollsActionAnswerDelete(answer))} />
            <Event event={EVENT_TYPE_RESTORED} handler={pollsGet} />
            {children}
        </PollsContext.Provider>
    );
};

export const usePollsContext = () => useContext(PollsContext);
